import { getDmpPreview } from "@/services/survey";
import router from "@/router";
import { VuexModule, Module, Mutation, Action } from "vuex-module-decorators";

import {
  LoginCredentials,
  LoginSuccess,
  UserProfile,
  UserRole,
  SurveyStatusResult,
  SurveyType,
  SurveyStatus,
  User,
} from "@/interfaces";
import { appLogin, getProfile, getStatus, submitSurveys, getUsers, getSingleUser } from "@/services";

import store from "@/store";

export interface AuthState {
  token: string | null;
  profile: UserProfile | null;
  errorMessage: string | null;
  loading: boolean;
  status: SurveyStatusResult | null;
  loadingStatus: boolean;
  loadingDMP: boolean;
  sending: boolean;
  dmpLink: string | null;
  isSSO: boolean;
}

export const initialState: AuthState = {
  token: null,
  profile: null,
  errorMessage: null,
  loading: false,
  status: null,
  loadingStatus: false,
  loadingDMP: false,
  sending: false,
  dmpLink: null,
  isSSO: false,
};

@Module({ name: "auth", namespaced: true, store })
export class Auth extends VuexModule implements AuthState {
  token: string | null = null;
  profile: UserProfile | null = null;
  errorMessage: string | null = null;
  loading = false;
  status: SurveyStatusResult | null = null;
  previewStatus: SurveyStatusResult | null = null;
  previewUser: UserProfile | null = null;
  loadingStatus = false;
  loadingUser = false;
  loadingUsers = false;
  loadingDMP = false;
  sending = false;
  dmpLink: string | null = null;
  users: User[] = [];
  isSSO = false;

  @Action({ rawError: true })
  async login({ email, password }: LoginCredentials) {
    try {
      this.context.commit("setLoading", true);
      const result = await appLogin(email, password);
      this.context.commit("loginSuccess", result);
      const profile = await getProfile();
      this.context.commit("setProfile", profile);
      const status = await getStatus();
      this.context.commit("setStatus", status);
      this.context.commit("setLoading", false);
    } catch (error) {
      this.context.commit("loginFail", error);
      this.context.commit("setLoading", false);
      return Promise.reject(error);
    }
  }

  @Action({ rawError: true })
  async loginSSO(payload: LoginSuccess) {
    try {
      this.context.commit("setLoading", true);
      this.context.commit("loginSuccess", payload);
      this.context.commit("setIsSSO", true);
      const profile = await getProfile();
      this.context.commit("setProfile", profile);
      const status = await getStatus();
      this.context.commit("setStatus", status);
      this.context.commit("setLoading", false);
    } catch (error) {
      this.context.commit("setIsSSO", false);
      this.context.commit("loginFail", error);
      this.context.commit("setLoading", false);
      return Promise.reject(error);
    }
  }

  @Action({ rawError: true })
  async sendSurveys() {
    try {
      this.context.commit("setSending", true);
      const result = await submitSurveys();
      await this.context.dispatch("getStatus");
      return result;
    } catch (error) {
      return Promise.reject(error);
    } finally {
      this.context.commit("setSending", false);
    }
  }

  @Action({ rawError: true })
  async getStatus() {
    try {
      this.context.commit("setLoadingStatus", true);
      const result = await getStatus();
      this.context.commit("setStatus", result);
    } catch (error) {
      this.context.commit("loginFail", error);
      return Promise.reject(error);
    } finally {
      this.context.commit("setLoadingStatus", false);
    }
  }

  @Action({ rawError: true })
  async getUser(id: number) {
    try {
      this.context.commit("setLoadingUser", true);
      const result = await getSingleUser(id);
      this.context.commit("setPreviewStatus", result.status);
      this.context.commit("setPreviewUser", result.user);
    } catch (error) {
      return Promise.reject(error);
    } finally {
      this.context.commit("setLoadingUser", false);
    }
  }

  @Action({ rawError: true })
  async getUsers() {
    try {
      this.context.commit("setLoadingUsers", true);
      const result = await getUsers();
      this.context.commit("setUsers", result.users);
    } catch (error) {
      return Promise.reject(error);
    } finally {
      this.context.commit("setLoadingUsers", false);
    }
  }

  @Action({ rawError: true })
  async getDMP() {
    try {
      this.context.commit("setLoadingDMP", true);
      const preview = await getDmpPreview();
      const lastDmp = preview.preview_url;
      this.context.commit("setDmp", lastDmp);
    } catch (error) {
      return Promise.reject(error);
    } finally {
      this.context.commit("setLoadingDMP", false);
    }
  }

  @Mutation
  setDmp(lastDmp: string) {
    this.dmpLink = lastDmp;
  }

  @Mutation
  loginSuccess(payload: LoginSuccess) {
    this.token = payload.token_type + " " + payload.token;
  }

  @Mutation
  setIsSSO(isSSO: boolean) {
    this.isSSO = isSSO;
  }

  @Mutation
  setProfile(profile: UserProfile) {
    this.profile = profile;
  }

  @Mutation
  private loginFail(error: string) {
    this.token = null;
    this.profile = null;
    this.loading = false;
    this.errorMessage = error;
  }

  @Mutation
  resetAuthState() {
    localStorage.removeItem("vuex");
  }

  @Mutation
  logout() {
    if (this.token === null) {
      return;
    }

    // tslint:disable-next-line: forin
    for (const k in initialState) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      this[k] = initialState[k];
    }

    // const r = router.currentRoute.fullPath;
    router.replace({ name: "login" });
  }

  @Mutation
  setLoading(status: boolean) {
    this.loading = status;
  }

  @Mutation
  setSending(sending: boolean) {
    this.sending = sending;
  }

  @Mutation
  setLoadingStatus(status: boolean) {
    this.loadingStatus = status;
  }

  @Mutation
  setLoadingUsers(status: boolean) {
    this.loadingUsers = status;
  }

  @Mutation
  setLoadingUser(status: boolean) {
    this.loadingUser = status;
  }

  @Mutation
  setLoadingDMP(loading: boolean) {
    this.loadingDMP = loading;
  }

  @Mutation
  setStatus(status: SurveyStatusResult) {
    this.status = status;
  }

  @Mutation
  setPreviewStatus(status: SurveyStatusResult) {
    this.previewStatus = status;
  }

  @Mutation
  setPreviewUser(user: UserProfile) {
    this.previewUser = user;
  }

  @Mutation
  setUsers(users: User[]) {
    this.users = users;
  }

  get isAdministrator() {
    if (this.profile !== null) {
      return this.profile.role === UserRole.admin;
    }

    return false;
  }

  get authenticated() {
    return this.token !== null;
  }

  get authToken() {
    return this.token;
  }

  get statusBySection() {
    return (sectionType: SurveyType) => {
      if (this.status) {
        let elements: any = [];

        this.status.sections.forEach(section => {
          elements = [...elements, ...section.elements];
        });

        const find = elements.find((st: SurveyStatus) => st.survey && st.survey === sectionType);

        return find;
      }

      return undefined;
    };
  }
}
